import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { altioraToast, toastApiError } from "@/components/altiora/toast";
import {
  PageShell, PageHeader, Card, Metric, Pill,
  NAVY, CHAMPAGNE, SLATE,
} from "@/components/altiora/PremiumUI";
import { mailto } from "@/lib/orgContact";
import {
  CheckCircle2, Settings, TrendingUp, Users, Clock, Wallet,
  ChevronRight, Sparkles, ExternalLink, Wrench,
} from "lucide-react";

// Local statuspill retained (dark navy variant, semantic)
function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
      ok ? "border-[#2D5A40] bg-[#14291E] text-[#059669]"
         : "border-[#7A521F] bg-[#33220D] text-[#F2C98A]"
    }`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${ok ? "bg-[#059669]" : "bg-[#F2C98A]"}`} />
      {label}
    </span>
  );
}

export default function Finance() {
  const { lang } = useI18n();
  const [showDev, setShowDev] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [txns, setTxns] = useState([]);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [interval, setInterval] = useState("month");
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);

  const load = async () => {
    try {
      const [p, s, c, tx] = await Promise.all([
        api.get("/finance/plans"),
        api.get("/finance/subscription"),
        api.get("/finance/cockpit"),
        api.get("/finance/transactions"),
      ]);
      setPlans(p.data.plans || []);
      setSubscription(s.data);
      setCockpit(c.data);
      setTxns(tx.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const checkout = async (plan_id) => {
    setCheckoutBusy(plan_id);
    try {
      const { data } = await api.post("/finance/checkout", {
        plan_id, interval,
        origin_url: window.location.origin,
        coupon: couponCode || null,
      });
      window.location.href = data.checkout_url;
    } catch {
      toastApiError(null, lang === "fr" ? "Impossible d'ouvrir le paiement" : "Cannot open payment");
      setCheckoutBusy(null);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/finance/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch {
      altioraToast.error(lang === "fr" ? "Espace de gestion indisponible" : "Management portal unavailable");
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post("/finance/coupon/validate", { code: couponCode.trim() });
      setCouponInfo(data);
      altioraToast.success(lang === "fr" ? `Code appliqué (${data.percent_off ? data.percent_off + "%" : "réduction"})` : "Code applied");
    } catch {
      setCouponInfo(null);
      altioraToast.error(lang === "fr" ? "Code invalide" : "Invalid code");
    }
  };

  const activePlan = plans.find((p) => p.id === subscription?.plan_id);

  return (
    <PageShell testid="finance-root">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow={lang === "fr" ? "ALTIORA · Système financier" : "ALTIORA · Finance"}
          title={lang === "fr" ? "Système financier ALTIORA" : "ALTIORA Financial System"}
          subtitle={lang === "fr"
            ? "Vue consolidée : abonnements, revenus, transactions."
            : "Consolidated view: subscriptions, revenue, transactions."}
          testid="finance-header" />

        <div className="space-y-6 mt-4">
        {/* Connection status */}
        <Card className="p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute -inset-2 rounded-full bg-[#059669]/10 blur-md" />
              <CheckCircle2 className="relative h-6 w-6 text-[#059669]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em]"
                       style={{ color: CHAMPAGNE }}>
                {lang === "fr" ? "Statut des paiements" : "Payments status"}
              </div>
              <div className="mt-0.5 font-serif text-lg tracking-tight"
                       style={{ color: NAVY }}>
                {lang === "fr" ? "Compte financier ALTIORA connecté" : "ALTIORA financial account connected"}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:ml-6">
            <StatusPill ok={true} label={lang === "fr" ? "Connecté" : "Connected"} />
            <StatusPill ok={true} label={lang === "fr" ? "Vérifié" : "Verified"} />
            <StatusPill ok={subscription?.active} label={
              subscription?.active
                ? (lang === "fr" ? (subscription.status === "trialing" ? "Essai actif" : "Abonnement actif") : "Active")
                : (lang === "fr" ? "En attente" : "Pending")
            } />
          </div>
          <div className="md:ml-auto flex items-center gap-2">
            {subscription?.active && (
              <button
                onClick={openPortal}
                data-testid="finance-portal-btn"
                className="inline-flex items-center gap-2 border border-[#E4E1D8] hover:border-[#0B2545] text-[#1F2937] text-xs font-medium px-4 py-2 rounded-md altiora-transition"
              >
                <Settings className="h-3.5 w-3.5" strokeWidth={1.8} />
                {lang === "fr" ? "Gérer l'abonnement" : "Manage subscription"}
              </button>
            )}
          </div>
        </Card>

        {/* Cockpit */}
        <div>
          <div className="mb-4 font-serif text-xl tracking-tight" style={{ color: NAVY }}>
            {lang === "fr" ? "Cockpit financier" : "Financial cockpit"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Metric testid="finance-stat-revenue"
                       label={lang === "fr" ? "Revenus du mois" : "Revenue this month"}
                       value={(cockpit?.revenue_month || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}
                       hint={lang === "fr" ? "après taxes gérées par ALTIORA" : "taxes managed by ALTIORA"} />
            <Metric testid="finance-stat-active"
                       label={lang === "fr" ? "Abonnements actifs" : "Active subscriptions"}
                       value={cockpit?.active_subscriptions ?? "—"}
                       hint={lang === "fr" ? "y compris les essais" : "including trials"} />
            <Metric testid="finance-stat-new"
                       label={lang === "fr" ? "Nouveaux clients (mois)" : "New customers (month)"}
                       value={cockpit?.new_customers_month ?? "—"} />
            <Metric testid="finance-stat-pending"
                       label={lang === "fr" ? "Paiements en attente" : "Pending payments"}
                       value={cockpit?.pending_payments ?? 0}
                       accent="#B45309" />
          </div>
        </div>

        {/* Current subscription card */}
        {subscription?.active && activePlan && (
          <div className="rounded-lg border border-[#0B2545]/40 bg-gradient-to-br from-[#0B2545]/5 via-transparent to-transparent p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Plan actuel" : "Current plan"}
                </div>
                <div className="mt-1 font-display text-2xl text-[#1F2937] tracking-tight">
                  {activePlan[`name_${lang}`]}
                </div>
                <div className="mt-1 text-sm text-[#5B5B62]">
                  {activePlan[`tagline_${lang}`]}
                </div>
              </div>
              <div className="text-right text-xs text-[#5B5B62]">
                {subscription.trial_end && (
                  <div>
                    {lang === "fr" ? "Essai jusqu'au " : "Trial until "}
                    <span className="text-[#1F2937] font-mono">{new Date(subscription.trial_end).toLocaleDateString()}</span>
                  </div>
                )}
                {subscription.current_period_end && (
                  <div className="mt-1">
                    {lang === "fr" ? "Prochaine facturation " : "Next billing "}
                    <span className="text-[#1F2937] font-mono">{new Date(subscription.current_period_end).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Plans */}
        {!subscription?.active && (
          <div>
            <div className="mb-6 flex items-baseline justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Choisir un plan" : "Choose a plan"}
                </div>
                <div className="mt-1 font-display text-xl text-[#1F2937] tracking-tight">
                  {lang === "fr" ? "Un plan qui grandit avec votre organisation" : "A plan that grows with your organisation"}
                </div>
              </div>
              <div className="inline-flex items-center rounded-full border border-[#E4E1D8] bg-white p-1">
                {[
                  { k: "month", fr: "Mensuel", en: "Monthly" },
                  { k: "year",  fr: "Annuel",  en: "Yearly" },
                ].map((b) => (
                  <button
                    key={b.k}
                    onClick={() => setInterval(b.k)}
                    data-testid={`finance-interval-${b.k}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-widest altiora-transition ${
                      interval === b.k ? "bg-[#0B2545] text-white" : "text-[#5B5B62] hover:text-[#1F2937]"
                    }`}
                  >
                    {b[lang]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((p) => {
                const price = p.amount_display[`cad_${interval === "year" ? "yearly" : "monthly"}`];
                const isContact = p.cta === "contact_sales";
                return (
                  <div
                    key={p.id}
                    data-testid={`finance-plan-${p.id}`}
                    className={`rounded-lg border p-6 md:p-7 altiora-transition ${
                      p.highlight
                        ? "border-[#0B2545] bg-gradient-to-b from-[#0B2545]/8 to-transparent"
                        : "border-[#E4E1D8] bg-white hover:border-[#B8B5AC]"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-display text-lg text-[#1F2937] tracking-tight">
                        {p[`name_${lang}`].replace(/^ALTIORA /, "")}
                      </div>
                      {p.highlight && (
                        <span className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-[#0B2545] text-white">
                          {lang === "fr" ? "Populaire" : "Popular"}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-[#5B5B62] min-h-[32px]">{p[`tagline_${lang}`]}</div>
                    <div className="mt-6 flex items-baseline gap-2">
                      {price ? (
                        <>
                          <span className="font-display text-4xl text-[#1F2937]">
                            {price.toLocaleString("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 })}
                          </span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                            /{interval === "year" ? (lang === "fr" ? "an" : "yr") : (lang === "fr" ? "mois" : "mo")}
                          </span>
                        </>
                      ) : (
                        <span className="font-display text-2xl text-[#1F2937]">
                          {lang === "fr" ? "Sur devis" : "Contact"}
                        </span>
                      )}
                    </div>
                    {p.trial_days > 0 && !isContact && (
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#059669]">
                        {lang === "fr" ? `${p.trial_days} jours d'essai offerts` : `${p.trial_days}-day free trial`}
                      </div>
                    )}
                    <ul className="mt-6 space-y-2.5 text-sm text-[#1F2937]">
                      {(p[`features_${lang}`] || []).map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 mt-1 text-[#0B2545] flex-shrink-0" strokeWidth={1.8} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid={`finance-cta-${p.id}`}
                      onClick={() => isContact ? window.location.href = mailto("partners", "ALTIORA Institution") : checkout(p.id)}
                      disabled={checkoutBusy === p.id}
                      className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium altiora-transition ${
                        p.highlight
                          ? "bg-[#0B2545] hover:bg-[#123163] text-white"
                          : "border border-[#E4E1D8] hover:border-[#0B2545] text-[#1F2937]"
                      } disabled:opacity-50`}
                    >
                      {isContact
                        ? (lang === "fr" ? "Contacter l'équipe" : "Contact sales")
                        : (checkoutBusy === p.id ? "…" : (lang === "fr" ? "Commencer" : "Get started"))}
                      <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Coupon */}
            <div className="mt-6 rounded-lg border border-[#E4E1D8] bg-white p-5 flex items-center gap-3">
              <Sparkles className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                {lang === "fr" ? "Code promotionnel" : "Promo code"}
              </div>
              <input
                data-testid="finance-coupon-input"
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="ALTIORA2026"
                className="flex-1 bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] font-mono focus:border-[#0B2545] focus:outline-none"
              />
              <button
                data-testid="finance-coupon-apply"
                onClick={applyCoupon}
                className="text-xs font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#123163]"
              >
                {lang === "fr" ? "Appliquer" : "Apply"}
              </button>
              {couponInfo && (
                <span className="text-xs text-[#059669] font-mono">
                  ✓ {couponInfo.percent_off ? couponInfo.percent_off + "%" : ""}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Recent transactions */}
        <div className="rounded-lg border border-[#E4E1D8] bg-white">
          <div className="px-6 py-4 border-b border-[#E4E1D8] flex items-baseline justify-between">
            <div className="font-display text-lg text-[#1F2937] tracking-tight">
              {lang === "fr" ? "Dernières transactions" : "Recent transactions"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">
              {txns.length} · {lang === "fr" ? "au total" : "total"}
            </div>
          </div>
          <div className="divide-y divide-[#E4E1D8]">
            {txns.length === 0 ? (
              <div className="px-6 py-8 text-sm text-[#8B8B8F] font-mono">
                {lang === "fr" ? "Aucune transaction encore. Elles apparaîtront ici automatiquement." : "No transactions yet."}
              </div>
            ) : (
              txns.slice(0, 10).map((tx) => (
                <div key={tx.session_id} data-testid="finance-txn-row" className="px-6 py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-[#1F2937]">{tx.plan_id}</div>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">
                      {new Date(tx.created_at).toLocaleString()} · {tx.currency?.toUpperCase()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[#1F2937]">
                      {(tx.amount / 100).toLocaleString("fr-CA", { style: "currency", currency: (tx.currency || "cad").toUpperCase() })}
                    </div>
                    <div className={`mt-0.5 font-mono text-[10px] uppercase tracking-widest ${
                      tx.payment_status === "paid" ? "text-[#059669]" :
                      tx.payment_status === "pending" ? "text-[#F2C98A]" : "text-[#E88A93]"
                    }`}>
                      {tx.payment_status}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Developer mode (hidden by default) */}
        <div className="rounded-lg border border-dashed border-[#E4E1D8] p-4">
          <button
            data-testid="finance-dev-toggle"
            onClick={() => setShowDev(!showDev)}
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#8B8B8F] hover:text-[#5B5B62]"
          >
            <Wrench className="h-3 w-3" strokeWidth={1.8} />
            {lang === "fr" ? "Mode développeur" : "Developer mode"}
            <ChevronRight className={`h-3 w-3 transition-transform ${showDev ? "rotate-90" : ""}`} strokeWidth={1.8} />
          </button>
          {showDev && (
            <div className="mt-4 space-y-2 text-xs font-mono text-[#8B8B8F]">
              <div>· {lang === "fr" ? "Environnement : test (bac à sable)" : "Environment: test (sandbox)"}</div>
              <div>· {lang === "fr" ? "Carte de test : 4242 4242 4242 4242 / date future / CVC 123" : "Test card: 4242 4242 4242 4242 / future date / CVC 123"}</div>
              <div>
                · <a
                  href="https://dashboard.stripe.com/onboard_sandbox/YWNjdF8xVHFIejdBeThaYmFwbmpYLDE3ODM5ODQxNDMv1000C62rMzO"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#0B2545] hover:underline inline-flex items-center gap-1"
                >
                  {lang === "fr" ? "Activer le compte pour la production" : "Activate account for production"}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </PageShell>
  );
}
